import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type Dispatch,
  type HTMLAttributes,
  type Ref,
  type SetStateAction
} from 'react';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import {
  Button,
  Card,
  Col,
  Form,
  Nav,
  Row,
  Spinner
} from 'react-bootstrap';
import { useOutletContext, useParams } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import OptionLayoutPlace from '@/app/common/components/OptionsLayout/OptionLayoutPlace';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import geoLanguagesData from '@/app/common/api/backups/languages.json';
import geoLocationsData from '@/app/common/api/backups/locations.json';

import { skipToken } from '@reduxjs/toolkit/query/react';
import gapApi, {
  useLazyGetGapDataCountQuery,
  useGetGapDataMutation,
  useGetGapVennQuery,
  useGetTopOpporTunityQuery,
  useGetDomainCompetitorsMutation,
  useExportTableMutation
} from '@/app/KeywordResearch/api/gapApi';
import { Formik, type FormikHelpers } from 'formik';
import * as yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '@/components/common/Flex';
import numberHumanizer from '@/helpers/numberHumanizer';
import {
  intentMap,
  keywordGapColumnConfig,
  purposeMap
} from '@/app/KeywordResearch/config/keywordGapColumnConfig';
import FalconCloseButton from '@/components/common/FalconCloseButton';
import FiltersComponentSkeleton from '../../common/components/filters/FiltersComponentSkeleton';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import NoKeywordGapData from '../components/keyword-gap/NoKeywordGapData';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery,
  variantByIndex
} from '@/helpers/utils';
import { warningMessage } from '@/utils/toast';
import VennDetailsDrawer from '../components/keyword-gap/VennDetailsDrawer';
import { hostApiFormatter } from '../components/keyword-gap/hostApiFormatter';
import KeywordOverlapCard from '../components/keyword-gap/KeywordOverlapCard';
import GrowthOpportunitiesCard from '../components/keyword-gap/GrowthOpportunitiesCard';
import GapReportModal from '../components/keyword-gap/GapReportModal';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import useDomain from '@/hooks/useDomain';
import GapCustomPosFilters from '../components/keyword-gap/GapCustomPosFilters';
import GapCustomDifficultyFilters from '../components/keyword-gap/GapCustomDifficultyFilters';
import GapCustomSearchVolumeFilters from '../components/keyword-gap/GapCustomSearchVolumeFilters';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import classNames from 'classnames';

type BreadcrumbItem = { text: string; href: string };
type HostItem = { host: string; i: number };
type HostApiItem = { host: string; i?: number };
type SelectOption = { value: string; label: string; flag?: string };
type KeywordGapFormValues = { hosts: HostItem[] };
type NavItemProps = {
  item: { key: string; value: { label: string; tooltip?: string } };
  count?: number | null;
  current: string;
  set: (key: string) => void;
  className?: string;
};
type OptionLayoutProps = {
  data: { label: string };
  innerProps: HTMLAttributes<HTMLDivElement>;
  innerRef: Ref<HTMLDivElement>;
  isSelected: boolean;
};

type AgGridWrapperProps = Record<string, unknown>;
type AgGridSkeletonProps = Record<string, unknown>;
type FiltersComponentProps = Record<string, unknown>;
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;
const TypedAgGridSkeleton = AgGridSkeleton as ComponentType<AgGridSkeletonProps>;
const TypedFiltersComponent =
  FiltersComponent as unknown as ComponentType<FiltersComponentProps>;

const KeywordGap = () => {
  const dispatch = useDispatch();
  const maxHosts = 4;
  const [submittedHosts, setSubmittedHosts] = useState<HostItem[] | null>(null);
  const [, setBreadcrumbData] = useOutletContext<
    [BreadcrumbItem[], Dispatch<SetStateAction<BreadcrumbItem[]>>]
  >();
  const { domain } = useDomain();
  useGetUserInfoQuery();
  const access = useToolAccess('keyword_gap');
  const { loc, lang } = useParams();
  const latestKeyword: {
    location_code?: string;
    language_code?: string;
  } | null = null;
  const navItemsMap = {
    missing: {
      label: 'فراموش شده',
      tooltip:
        'کلماتی که در آن همه رقبا دارای رتبه هستن، اما سایت شما هیچ رتبه ای نداره.'
    },
    unuse: {
      label: 'استفاده نشده',
      tooltip:
        'کلماتی که سایت شما رتبه نداره ولی حداقل یکی از رقیب ها رتبه داره.'
    },
    weak: {
      label: 'ضعیف',
      tooltip: 'کلماتی که سایت شما در مقایسه با تمام رقبا رتبه پایین‌تری داره.'
    },
    strong: {
      label: 'قوی',
      tooltip: 'کلماتی که سایت شما در مقایسه با تمام رقبا رتبه بالاتری داره.'
    },
    shared: { label: 'مشترک', tooltip: 'کلمات مشترک بین همه دامنه ها.' },
    unique: {
      label: 'یکتا',
      tooltip: 'کلماتی که شما در آن رتبه داری ولی هیچ یک از رقبا رتبه ندارن.'
    },
    all: { label: 'همه', tooltip: 'لیست همه کلمات کلیدی بین شما و رقبا.' }
  };
  const [geoLocationsCountries, setGeoLocationsCountries] = useState<
    SelectOption[]
  >();
  const [geoLanguages, setGeoLanguages] = useState<SelectOption[]>();

  const [selectedPlace, setSelectedPlace] = useState<SelectOption>({
    value: 'ir',
    label: 'ایران',
    flag: `https://static.mizfa.tools/flags/ir.webp`
  });
  const isLgUp = useMediaQuery(breakpointsMediaQuery.lg);

  const [selectedLanguage, setSelectedLanguage] = useState<SelectOption>({
    value: 'fa',
    label: 'فارسی'
  });
  useEffect(() => {
    if (geoLocationsData) {
      const geoCountries: SelectOption[] = [];
      geoLocationsData.data.map(({ code, fa_name }) =>
        geoCountries.push({
          value: code,
          label: fa_name,
          flag: `https://static.mizfa.tools/flags/${code}.webp`
        })
      );

      setGeoLocationsCountries(geoCountries);

      if (loc && loc !== 'ir' && latestKeyword) {
        let geo: { code?: string; fa_name?: string } | undefined;
        if (loc === 'last')
          geo = geoLocationsData.data.find(
            item =>
              item.code ===
              (latestKeyword as {
                location_code?: string;
                language_code?: string;
              } | null)?.location_code
          );
        else
          geo = geoLocationsData.data.find(
            item => item.code === loc.toLowerCase()
          );
        setSelectedPlace({
          value: geo?.code || '',
          label: geo?.fa_name || '',
          flag: `https://static.mizfa.tools/flags/${geo?.code}.webp`
        });
      }
    }
  }, [geoLocationsData]);

  useEffect(() => {
    if (geoLanguagesData) {
      const geoLanguages: SelectOption[] = [];
      geoLanguagesData.data.map(({ code, fa_name }) =>
        geoLanguages.push({
          value: code,
          label: fa_name
        })
      );

      setGeoLanguages(geoLanguages);

      if (lang && lang !== 'fa' && latestKeyword) {
        let geo: { code?: string; fa_name?: string } | undefined;
        if (lang === 'last')
          geo = geoLanguagesData.data.find(
            item =>
              item.code ===
              (latestKeyword as {
                location_code?: string;
                language_code?: string;
              } | null)?.language_code
          );
        else geo = geoLanguagesData.data.find(item => item.code === lang);

        setSelectedLanguage({
          value: geo?.code || '',
          label: geo?.fa_name || ''
        });
      }
    }
  }, [geoLanguagesData]);
  const [getGapDataFunc, { data: mutationData, isSuccess }] =
    useGetGapDataMutation();
  const [exportTable] = useExportTableMutation();
  const [activeMode, setActiveMode] = useState('missing');
  const [activeModeOppurtunity, setActiveModeOpputinity] = useState('missing');
  const [gapContext, setGapContext] = useState<{
    location?: string;
    language?: string;
  } | null>(null);
  useEffect(() => {
    setBreadcrumbData([{ text: 'کیورد گپ', href: '#' }]);
  }, []);
  const lastMutationDataRef = useRef<{
    counts?: Record<string, number>;
  } | null>(null);
  const [, setForceUpdate] = useState(0);

  useEffect(() => {
    if (isSuccess && mutationData?.data) {
      lastMutationDataRef.current = mutationData.data;
      // TRIGGER RERENDER ON SAVE
      setForceUpdate(prev => prev + 1);
    }
  }, [isSuccess, mutationData]);
  const [getGapDataCount, { data: gridData, isFetching, isLoading }] =
    useLazyGetGapDataCountQuery();
  const gapQuery = useMemo(() => {
    if (!submittedHosts || !gapContext) return null;
    return {
      hosts: hostApiFormatter(submittedHosts),
      location: gapContext.location,
      language: gapContext.language
    };
  }, [submittedHosts, gapContext]);
  const missingArgs = useMemo(
    () => (gapQuery ? { ...gapQuery, model: 'missing', limit: 7 } : skipToken),
    [gapQuery]
  );
  const weakArgs = useMemo(
    () => (gapQuery ? { ...gapQuery, model: 'weak', limit: 7 } : skipToken),
    [gapQuery]
  );
  const vennQuery = useGetGapVennQuery(gapQuery ?? skipToken);
  const missingOpportunityQuery = useGetTopOpporTunityQuery(missingArgs);
  const weakOpportunityQuery = useGetTopOpporTunityQuery(weakArgs);
  useEffect(() => {
    if (!isFetching && gridData?.code === 4001) {
      warningMessage('لطفا دامنه تکراری وارد نکن.');
    }
  }, [gridData?.code]);
  useEffect(() => {
    if (submittedHosts && gridData)
      getGapDataCount({
        model: activeMode,
        page: 1,
        perPage: 50,
        location: selectedPlace?.value,
        language: selectedLanguage?.value,
        hosts: hostApiFormatter(submittedHosts)
      });
  }, [activeMode, submittedHosts]);
  const [
    getDomainCompetitors,
    { data: domainCompetitors, isLoading: isLoadingDomainCompetitors }
  ] = useGetDomainCompetitorsMutation();
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return (
      <ToolAccessDenied access={access} tool_label="ابزار کیورد گپ" />
    );
  }
  const queryError = getActiveQueryError([
    {
      isError: access.isError,
      error: access.error as never,
      refetch: access.refetch,
      isFetching: access.isFetching,
    },
  ]);

  if (queryError) return <DataLoadError {...queryError} />;

  const initialValues: KeywordGapFormValues = {
    hosts: domain
      ? [
          { host: domain, i: 0 },
          ...Array.from({ length: maxHosts - 1 }).map((_, i) => ({
            host: '',
            i: i + 1
          }))
        ]
      : []
  };

  return (
    <Formik<KeywordGapFormValues>
      onSubmit={async (
        values,
        _action: FormikHelpers<KeywordGapFormValues>
      ) => {
        try {
          const nextHosts = hostApiFormatter(values.hosts);
          const nextLocation = selectedPlace?.value;
          const nextLanguage = selectedLanguage?.value;
          const sameGap =
            gapQuery !== null &&
            gapQuery.location === nextLocation &&
            gapQuery.language === nextLanguage &&
            gapQuery.hosts.length === nextHosts.length &&
            gapQuery.hosts.every((host, index) => host === nextHosts[index]);
          setSubmittedHosts(values.hosts);
          setGapContext({
            location: nextLocation,
            language: nextLanguage
          });
          getGapDataCount({
            model: activeMode,
            page: 1,
            perPage: 50,
            location: nextLocation,
            language: nextLanguage,
            hosts: nextHosts
          });
          if (sameGap) {
            void vennQuery.refetch();
            void missingOpportunityQuery.refetch();
            void weakOpportunityQuery.refetch();
          }

          if (submittedHosts) {
            dispatch(gapApi.util.invalidateTags(['KeywordsGap']));
          }
        } catch (error) {
          console.log('خطا در گرفتن اعداد ', error);
        }
      }}
      initialValues={initialValues}
      validationSchema={yup.object().shape({
        hosts: yup
          .array()
          .of(
            yup.object().shape({
              host: yup
                .string()
                .trim()
                .test(
                  'is-valid-domain',
                  // 'دامنه معتبر نیست.',
                  'دامنه نباید با http، https یا www شروع شود و باید یک دامنه معتبر باشد.',
                  value => {
                    if (!value || value.trim().length === 0) return true; // Skip empty strings
                    return (
                      /^(?!https?:\/\/|www\.)[^\s/$.?#].[^\s]*$/.test(value) &&
                      value.includes('.')
                    );
                  }
                )
                .test(
                  'no-slash',
                  'لطفا "/" و هرآنچه بعد از آن را پاک کنید. ',
                  value => {
                    if (!value || value.trim().length === 0) return true; // Skip empty strings

                    return !value?.includes('/');
                  } // بررسی عدم وجود "/"
                )
                .max(50, 'دامنه شما نمیتواند بیشتر از ۵۰ کاراکتر باشد.'),
              i: yup.number()
            })
          )
          .test(
            'minimum-two-valid-domains',
            'برای مشاهده نتیجه حداقل دو دامنه معتبر وارد کن.',
            value => {
              const validDomains = (value ?? []).filter(
                v =>
                  v?.host &&
                  /^(?!https?:\/\/|www\.)[^\s/$.?#].[^\s]*$/.test(v?.host) &&
                  v?.host.includes('.')
              );
              return validDomains.length >= 2;
            }
          )
          .test(
            'no-duplicate-domains',
            'دامنه‌ها نباید تکراری باشند.',
            value => {
              const normalizedHosts = (value ?? [])
                .map(item => item?.host?.trim().toLowerCase())
                .filter(Boolean);

              return new Set(normalizedHosts).size === normalizedHosts.length;
            }
          )
      })}
    >
      {({
        values,
        errors,
        handleSubmit,
        setFieldTouched,
        setFieldValue,
        touched
      }) => {
        const [fetchedCompFor, setFetchedCompFor] = useState<string | null>(
          null
        );
        const hostErrors = errors.hosts as
          | Array<{ host?: string }>
          | string
          | undefined;
        useEffect(() => {
          if (
            focusedIndex &&
            focusedIndex !== 0 &&
            values.hosts[0].host &&
            fetchedCompFor !== values.hosts[0].host &&
            (!hostErrors ||
              (Array.isArray(hostErrors) && !hostErrors[0]?.host))
          ) {
            getDomainCompetitors(
              {
                host: values.hosts[0].host,
                offset: 0
              } as never
            );
            setFetchedCompFor(values.hosts[0].host);
          }
        }, [focusedIndex]);
        return (
          <div id="keywordGap">
            <GapReportModal />
            <VennDetailsDrawer submittedHosts={submittedHosts} />
            <style>{`
      #keywordGap .ag-theme-custom {
      --ag-borders:0px !important;
      --ag-row-border-width:0px !important;
      box-shadow:unset !important;
      }
      `}</style>
            <form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <Row className="g-0 mt-4">
                {Array.from({ length: maxHosts }, (_, index) => index).map(
                  c => (
                    <Col
                      key={c}
                      xxl={2}
                      xl={12}
                      className="pe-xxl-2 mb-2 mb-xl-0"
                    >
                      <div className="d-flex fs--1 flex-row gap-2">
                        <Form.Group className="flex-1">
                          <div className="position-relative">
                            {
                              <div
                                style={{
                                  top: -20,
                                  left: 0,
                                  position: 'absolute'
                                }}
                              >
                                <SubtleBadge
                                  className="fs--2"
                                  bg={variantByIndex(c)}
                                  pill
                                >
                                  {c === 0 ? 'شما' : `رقیب ${c}`}
                                </SubtleBadge>
                              </div>
                            }
                            <Form.Control
                              size={'md' as 'lg'}
                              onBlur={() => {
                                setFieldTouched(`hosts[${c}]`, true);
                                setFocusedIndex(null);
                              }}
                              autoComplete="off"
                              isInvalid={Boolean(
                                values?.hosts[c].host?.trim() &&
                                errors.hosts &&
                                touched.hosts &&
                                touched?.hosts[c] &&
                                Array.isArray(hostErrors) &&
                                hostErrors[c]?.host
                              )}
                              isValid={Boolean(
                                (values?.hosts[c].host?.trim() &&
                                  touched.hosts &&
                                  touched.hosts[c] &&
                                  !errors.hosts) ||
                                (values?.hosts[c].host?.trim() &&
                                  errors.hosts &&
                                  touched?.hosts &&
                                  touched?.hosts[c] &&
                                  !(
                                    Array.isArray(hostErrors) &&
                                    hostErrors[c]?.host
                                  ))
                              )}
                              onChange={e => {
                                const hostsCopy = [...values.hosts];
                                hostsCopy[c] = { host: e.target.value, i: c };
                                setFieldValue('hosts', hostsCopy);
                              }}
                              // disabled={
                              //   c !== 0 &&
                              //   (!values.hosts[c - 1] ||
                              //     values.hosts[c - 1].length === 0)
                              // }
                              style={{ direction: 'ltr' }}
                              id={`hosts-${c}`}
                              onFocus={() => {
                                setFocusedIndex(c);
                              }}
                              name={`hosts[${c}].host`}
                              value={values.hosts[c].host || ''}
                              placeholder={c === 0 ? 'سایت شما' : `رقیب شما`}
                            />
                            {values.hosts[c].host &&
                              values.hosts[c]?.host.trim() !== '' && (
                                <FalconCloseButton
                                  size="md"
                                  noOutline
                                  onClick={() => {
                                    const hostsCopy = [...values.hosts];
                                    hostsCopy.splice(c, 1, {
                                      host: '',
                                      i: c
                                    });
                                    setFieldValue('hosts', hostsCopy);
                                  }}
                                  style={{
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    right: 7,
                                    fontSize: 9,
                                    position: 'absolute'
                                  }}
                                />
                              )}
                          </div>
                          {focusedIndex !== 0 &&
                            !isLoadingDomainCompetitors &&
                            Boolean(domainCompetitors?.data) &&
                            focusedIndex === c &&
                            values.hosts[c]?.host?.length === 0 && (
                              <Select
                                classNamePrefix="react-select"
                                styles={{
                                  group: (baseStyles, _state) => ({
                                    ...baseStyles,
                                    backgroundColor: getColor('gray-300'),
                                    padding: 0
                                  }),
                                  control: (_baseStyles, _state) => ({
                                    display: 'none'
                                  }),
                                  menu: (baseStyles, _state) => ({
                                    ...baseStyles,
                                    width: '100% !important'
                                  }),
                                  input: (baseStyles, _state) => ({
                                    ...baseStyles,
                                    color: 'var(--falcon-emphasise) !important',
                                    boxShadow: 'none !important'
                                  })
                                }}
                                components={{
                                  Option: OptionLayout
                                }}
                                menuIsOpen
                                noOptionsMessage={() => 'پیشنهادی یافت نشد.'}
                                options={[
                                  {
                                    label: 'رقیب پیشنهادی',
                                    options: [
                                      ...(
                                        (
                                          domainCompetitors?.data as
                                            | {
                                                competitors?: Array<{
                                                  count: number;
                                                  site: string;
                                                }>;
                                              }
                                            | undefined
                                        )?.competitors ?? []
                                      )
                                    ]
                                      ?.sort((a, b) => b.count - a.count)
                                      ?.map(comp => ({
                                        value: comp.site,
                                        label: comp.site
                                      }))
                                  }
                                ]}
                                formatGroupLabel={data => (
                                  // <div style={groupStyles}>
                                  <div className="d-flex gap-1 align-items-center">
                                    <span className="my-2 fs--1 text-1100">
                                      {data.label}
                                    </span>
                                    {/* <SubtleBadge className="fs--2" bg="info">
                                      {data.options.length - 1}
                                    </SubtleBadge> */}
                                  </div>
                                )}
                                onChange={(e: SelectOption | null) => {
                                  if (!e) return;
                                  const hostsCopy = [...values.hosts];
                                  hostsCopy[c] = { host: e.value, i: c };
                                  setFieldValue('hosts', hostsCopy);
                                }}
                              />
                            )}
                          <Form.Control.Feedback
                            type="invalid"
                            className="fs--2 ms-2 mt-1 mb-0 d-block"
                          >
                            {errors.hosts && touched.hosts
                              ? typeof errors.hosts === 'string'
                                ? ''
                                : touched?.hosts[c] &&
                                  Array.isArray(hostErrors) &&
                                  hostErrors[c]?.host
                              : ''}
                            &nbsp;
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>
                    </Col>
                  )
                )}
                <Col xxl={4} xl={12} className="d-flex align-items-start gap-2">
                  <div className="input-group position-relative justify-content-start input-group-md">
                    <div>
                      <Select
                        menuShouldScrollIntoView={true}
                        closeMenuOnSelect={true}
                        isSearchable={true}
                        onChange={(e: SelectOption | null) => {
                          if (!e) return;
                          setSelectedPlace(e);
                          const { official_locaguage_code } =
                            geoLocationsData.data.find(
                              item => item.code === e.value
                            ) ?? {};

                          let defaulrLanguage = {
                            code: 'en',
                            fa_name: 'انگلیسی'
                          };
                          if (official_locaguage_code) {
                            defaulrLanguage =
                              geoLanguagesData.data.find(
                                item => item.code === official_locaguage_code
                              ) ?? defaulrLanguage;
                          }

                          setSelectedLanguage({
                            value: defaulrLanguage.code,
                            label: defaulrLanguage.fa_name
                          });
                        }}
                        options={geoLocationsCountries}
                        classNamePrefix="react-select"
                        value={selectedPlace}
                        maxMenuHeight={300}
                        styles={{
                          control: (baseStyles, _state) => ({
                            ...baseStyles,
                            paddingLeft: '0px',
                            width: 105,
                            paddingRight: '10px',
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important',
                            cursor: 'pointer',
                            borderRadius: '0px 5px 5px 0px'
                          }),
                          menu: (baseStyles, _state) => ({
                            ...baseStyles,
                            width: '160px !important'
                          }),
                          input: (baseStyles, _state) => ({
                            ...baseStyles,
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important'
                          })
                        }}
                        components={{
                          Option: OptionLayoutPlace,
                          SingleValue: SingleValueFlag
                        }}
                      />
                    </div>
                    <div>
                      <Select
                        menuShouldScrollIntoView={true}
                        closeMenuOnSelect={true}
                        isSearchable={true}
                        onChange={(e: SelectOption | null) => {
                          if (e) setSelectedLanguage(e);
                        }}
                        options={geoLanguages}
                        classNamePrefix="react-select"
                        value={selectedLanguage}
                        maxMenuHeight={300}
                        styles={{
                          control: (baseStyles, _state) => ({
                            ...baseStyles,
                            paddingLeft: '0px',
                            width: 100,
                            paddingRight: '10px',
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important',
                            cursor: 'pointer',
                            borderRadius: '0px'
                          }),
                          menu: (baseStyles, _state) => ({
                            ...baseStyles,
                            width: '120px !important'
                          }),
                          input: (baseStyles, _state) => ({
                            ...baseStyles,
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important'
                          })
                        }}
                        components={{
                          Option: OptionLayoutPlace,
                          SingleValue: SingleValueFlag
                        }}
                      />
                    </div>
                    <ChildrensTooltip
                      className="d-none d-sm-block"
                      placement="top"
                      html="ریست کردن دامنه ها"
                    >
                      <Button
                        style={{
                          height: '100%',
                          marginRight: -1
                        }}
                        size={'md' as 'lg'}
                        className="rounded-0 border-300"
                        variant="outline-secondary"
                        onClick={() => {
                          setFieldValue('hosts', initialValues?.hosts);
                        }}
                      >
                        <FontAwesomeIcon size="lg" icon="eraser" />
                      </Button>
                    </ChildrensTooltip>
                    <>
                      <Button
                        size={'md' as 'lg'}
                        className="d-flex flex-1 justify-content-center gap-2 text-nowrap align-items-center border-0"
                        // onClick={() => handleSubmit()}
                        type="submit"
                        disabled={isFetching}
                        variant="falcon-primary"
                      >
                        {isFetching ? (
                          <>
                            <Spinner
                              style={{
                                width: '16px',
                                height: '16px'
                              }}
                              className="me-1"
                            />
                          </>
                        ) : (
                          'مقایسه'
                        )}
                      </Button>
                    </>
                  </div>
                </Col>
              </Row>

              <Form.Control.Feedback
                type="invalid"
                className="fs--2 mb-1 mb-lg-2 mt-lg-n3 ms-2 d-block"
              >
                {errors.hosts &&
                touched.hosts &&
                typeof errors.hosts === 'string'
                  ? errors.hosts
                  : ''}
                &nbsp;
              </Form.Control.Feedback>
            </form>

            {submittedHosts ? (
              <>
                <Row className="g-3 my-3">
                  <Col xs={12} xl={7} className="d-flex">
                    <KeywordOverlapCard
                      hosts={submittedHosts}
                      data={vennQuery.data}
                      isLoading={
                        !vennQuery.data &&
                        (vennQuery.isLoading ||
                          vennQuery.isFetching ||
                          vennQuery.isUninitialized)
                      }
                      isError={vennQuery.isError}
                      onRetry={() => {
                        void vennQuery.refetch();
                      }}
                    />
                  </Col>
                  <Col xs={12} xl={5} className="d-flex">
                    <GrowthOpportunitiesCard
                      active={activeModeOppurtunity}
                      onChange={setActiveModeOpputinity}
                      tabs={[
                        {
                          key: 'missing',
                          label: navItemsMap.missing.label,
                          tooltip: navItemsMap.missing.tooltip,
                          query: missingOpportunityQuery
                        },
                        {
                          key: 'weak',
                          label: navItemsMap.weak.label,
                          tooltip: navItemsMap.weak.tooltip,
                          query: weakOpportunityQuery
                        }
                      ]}
                    />
                  </Col>
                </Row>
                <Row className="g-3">
                  <Col xxl={12}>
                    {gridData?.data && !isLoading && !access.isLoading ? (
                      <>
                        <TypedFiltersComponent
                          selectItems={{
                            purpose: Object.entries(purposeMap).map(
                              ([k, v]) => ({
                                label: v,
                                value: k
                              })
                            ),
                            intent: Object.entries(intentMap).map(([k]) => ({
                              label: k,
                              value: k
                            }))
                          }}
                          columns={keywordGapColumnConfig(
                            gridData?.data?.columns as string[] | undefined,
                            submittedHosts
                          )}
                          SampleFilters={
                            <>
                              <GapCustomPosFilters
                                submittedHosts={submittedHosts}
                              />
                              <GapCustomDifficultyFilters />
                              <GapCustomSearchVolumeFilters />
                            </>
                          }
                        />
                      </>
                    ) : (
                      <FiltersComponentSkeleton />
                    )}
                  </Col>
                  <Col>
                    <div className="d-flex gap-2 w-100 w-md-50 mb-3 align-items-center gap-3">
                      <div className="text-nowrap font-bold">دامنه‌شما</div>
                      <Form.Select
                        style={{ maxWidth: 200 }}
                        value={submittedHosts[0].host}
                        onChange={({ target }) => {
                          const newHost = target.value;
                          const newHostIndex = submittedHosts.findIndex(
                            ({ host }) => host === newHost
                          );

                          if (newHostIndex !== -1) {
                            let submittedHostsCopy = [...submittedHosts];
                            const firstHost = submittedHostsCopy[0];
                            const selectedHost =
                              submittedHostsCopy[newHostIndex];
                            submittedHostsCopy[0] = selectedHost;
                            submittedHostsCopy[newHostIndex] = firstHost;
                            submittedHostsCopy = submittedHostsCopy.map(
                              ({ host }, i) => ({ host, i })
                            );
                            setSubmittedHosts(submittedHostsCopy);
                            setFieldValue('hosts', submittedHostsCopy);
                          }
                        }}
                      >
                        {submittedHosts
                          .filter(({ host }) => host)
                          .map(({ host }) => (
                            <option key={host} value={host}>
                              {host}
                            </option>
                          ))}
                      </Form.Select>
                    </div>
                    <Card>
                      <Card.Header
                        style={{ zIndex: 1 }}
                        as={Flex}
                        justifyContent="between"
                        alignItems="center"
                        className="ps-0 py-0 position-relative border-bottom"
                      >
                        <Nav
                          as="ul"
                          className={classNames(
                            'nav-tabs flex-nowrap border-0  chart-tab tab-active-caret',
                            {
                              'overflow-x-auto overflow-y-hidden': !isLgUp
                            }
                          )}
                        >
                          {Object.entries(navItemsMap).map(([key, value]) => (
                            <NavItem
                              current={activeMode}
                              set={setActiveMode}
                              count={
                                location.search.includes('FBy')
                                  ? (lastMutationDataRef.current?.counts?.[
                                      key
                                    ] ?? gridData?.data?.counts?.[key])
                                  : gridData?.data?.counts?.[key]
                              }
                              item={{ key, value }}
                              key={key}
                            />
                          ))}
                        </Nav>
                      </Card.Header>
                      <Card.Body className="p-0">
                        {gridData?.data &&
                        !isFetching &&
                        !access.isLoading ? (
                          <TypedAgGridWrapper
                            // otherOptions={{
                            //   getRowStyle: params => {
                            //     const totalRows = params.api.getDisplayedRowCount();
                            //     const zIndex = totalRows - params.node.rowIndex;
                            //     return {
                            //       zIndex
                            //     };
                            //   }
                            // }}
                            settings={{
                              pagination: true,
                              fullscreen: false,
                              changeHeight: false,
                              resetGrid: false,
                              saveGrid: false,
                              sideBar: false,
                              export: {
                                enabled: true,
                                fetchFunction: exportTable,
                                additionalData: {
                                  hosts: hostApiFormatter(submittedHosts),
                                  location: selectedPlace?.value,
                                  language: selectedLanguage?.value,
                                  model: activeMode
                                }
                              }
                            }}
                            height="700px"
                            columns={keywordGapColumnConfig(
                              gridData?.data?.columns as string[] | undefined,
                              submittedHosts
                            )}
                            total={gridData?.data?.total}
                            additionalData={{
                              hosts: hostApiFormatter(submittedHosts),
                              location: selectedPlace?.value,
                              language: selectedLanguage?.value,
                              model: activeMode
                            }}
                            fetchFunction={getGapDataFunc}
                            data={gridData?.data?.rows}
                          />
                        ) : (
                          <TypedAgGridSkeleton
                            height="750px"
                            // settings={{
                            //   pagination: true,
                            //   fullscreen: true,
                            //   changeHeight: true,
                            //   resetGrid: false,
                            //   saveGrid: false,
                            //   exportBtn: false
                            // }}
                          />
                        )}
                      </Card.Body>
                    </Card>
                    {/* <div ref={gridRef}>         
        </div> */}
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <NoKeywordGapData />
              </>
            )}
          </div>
        );
      }}
    </Formik>
  );
};

export default KeywordGap;

const NavItem = ({
  item,
  count,
  current,
  set,
  className = ''
}: NavItemProps) => {
  const isActive = current === item.key;
  return (
    <Nav.Item
      className="z-3"
      as="li"
      style={{ overflow: 'visible', position: 'relative' }}
    >
      <Nav.Link
        onClick={() => set(item.key)}
        className={
          className +
          ' mb-0 text-nowrap d-flex gap-1 align-items-center navLinkForceColor py-3 cursor-pointer '
        }
        active={isActive}
      >
        <span className={isActive ? 'fw-bold' : 'fw-normal'}>
          {item.value.label}
        </span>
        <div
          className={isActive ? 'fw-bold' : 'fw-normal'}
          style={{ fontSize: '0.65rem' }}
        >
          {count && (
            <>
              <span>(</span>
              {numberHumanizer(count)}
              <span>)</span>
            </>
          )}
        </div>
        {item?.value?.tooltip && (
          <HelpTooltip
            delayToRemove={0}
            html={item?.value?.tooltip}
            placement="top"
          />
        )}
      </Nav.Link>
    </Nav.Item>
  );
};
const OptionLayout = (props: OptionLayoutProps) => {
  const { innerProps, innerRef } = props;
  return (
    <>
      <style>{`
      .customized-searchbox-option:hover{
        background-color:var(--falcon-gray-link-hover-300) !important;
      }
      .customized-searchbox-option{
        background-color:var(--falcon-tertiary-bg) !important;
      }

      `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          backgroundColor: props.isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className="p-2 cursor-pointer border border-1  customized-searchbox-option"
      >
        <div className="fs--1 text-900">{props.data.label} </div>
      </div>
    </>
  );
};
